<template>
	<Col :span="24" :offset="0">
		<TypographyTitle class="pd-vertical" :level="3">{{ $t('account.linkedEmails.header') }}</TypographyTitle>
		<p v-if="!smtpReady" class="pd-vertical">
			{{ $t('account.linkedEmails.smtpNotReady') }}
			<router-link :to="{ name: 'settings-type', params: { id: 'email-alerts' } }">
				{{ $t('account.linkedEmails.smtpNotReadyLink') }}
			</router-link>.
		</p>
		<div class="scrollable-x">
			<Table :columns="columns" :data-source="accounts" :pagination="false" :loading="isLoading">
				<template #bodyCell="{ column, record }">
					<template v-if="column.dataIndex === 'email'">
						<Space>
							{{ record.email }}
							<Tag v-if="!record.verified" color="orange">
								{{ $t('account.linkedEmails.pendingVerification') }}
							</Tag>
							<Button
								v-if="!record.verified"
								type="primary"
								size="small"
								:disabled="!smtpReady"
								@click="accountResendVerification(record)"
							>
								{{ $t('account.linkedEmails.verifyButtonLabel') }}
							</Button>
						</Space>
					</template>
					<template v-else-if="column.dataIndex === 'alertsEnabled'">
						<Switch
							v-model:checked="record.alertsEnabled"
							size="small"
							:disabled="!record.verified || !smtpReady"
							@change="accountUpdate(record)"
						/>
					</template>
					<template v-else-if="column.dataIndex === 'alertLevel'">
						<Select
							v-if="record.verified"
							v-model:value="record.alertLevel"
							size="small"
							:options="levelOptions"
							:disabled="!record.alertsEnabled || !smtpReady"
							style="width: 120px"
							@change="accountUpdate(record)"
						/>
					</template>
					<template v-else-if="column.dataIndex === 'deliveryPeriod'">
						<Select
							v-if="record.verified"
							v-model:value="record.deliveryPeriodMinutes"
							size="small"
							:options="periodOptions"
							:disabled="!record.alertsEnabled || !smtpReady"
							style="width: 120px"
							@change="accountUpdate(record)"
						/>
					</template>
					<template v-else-if="column.dataIndex === 'delete'">
						<PopconfirmButton
							:title="$t('account.linkedEmails.deleteConfirmation')"
							:ok-text="$t('account.linkedEmails.deleteButtonLabel')"
							:cancel-text="$t('account.linkedEmails.cancelButtonLabel')"
							size="small"
							:disabled="!smtpReady"
							:onConfirm="() => accountDelete(record)"
						>
							{{ $t('account.linkedEmails.deleteButtonLabel') }}
						</PopconfirmButton>
					</template>
					<template v-else-if="column.dataIndex === 'test'">
						<Button
							v-if="record.verified"
							:disabled="!smtpReady"
							size="small"
							@click="accountTest(record)"
						>
							{{ $t('account.linkedEmails.testButtonLabel') }}
						</Button>
					</template>
				</template>
			</Table>
		</div>
		<Space class="pd-vertical">
			<Form layout="inline" :model="formState">
				<FormItem v-bind="validateInfosNewEmail">
					<InputRestricted
						v-model="formState.newEmail"
						:style="{ width: '300px' }"
						:maxlength="255"
						:disabled="!smtpReady"
						:placeholder="$t('account.linkedEmails.newEmailLabel')"
						:allowedFormatChecker="latinCharactersChecker"
						@keyup.enter="onAccountAdd"
					/>
				</FormItem>
				<FormItem>
					<Button type="primary" :disabled="!smtpReady" @click.prevent="onAccountAdd">
						{{ $t('account.linkedEmails.accountAddButtonLabel') }}
					</Button>
				</FormItem>
			</Form>
		</Space>
	</Col>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import {
	Col, Button, TypographyTitle,
	Table, Switch, Form, FormItem, Space, Select, Tag, message,
} from 'ant-design-vue';

const useForm = Form.useForm;

import { useI18n } from 'vue-i18n';

import ApiAlertsEmail from '@/api/ApiAlertsEmail';
import { toCamelCaseList } from '@/api/ApiHelpers';
import Consts from '@/api/Consts';
import Validators from '@/api/Validators';

import { showErrorMessage } from '@/components/common/errorMessage';

import InputRestricted from '@/components/common/InputRestricted';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';


export default {
	components: {
		Col, Button, TypographyTitle,
		Table, Switch, Form, FormItem, Space, Select, Tag,
		InputRestricted, PopconfirmButton,
	},
	setup() {
		const { t } = useI18n();
		const isLoading = ref(false);
		const validated = ref(false);
		const accounts = ref([]);
		const smtpReady = ref(false);
		const formState = reactive({
			newEmail: '',
		});
		const validateNewEmail = async (_rule, value) => {
			if (!value || !value.includes('@')) {
				return Promise.reject(t('account.linkedEmails.newEmailHelp'));
			}
			return Promise.resolve();
		};
		const rulesRef = reactive({
			newEmail: [{ validator: validateNewEmail }],
		});
		const { validate, validateInfos } = useForm(formState, rulesRef);

		const levelOptions = computed(() => [
			{ value: 'ERROR', label: t('account.linkedEmails.levels.ERROR') },
			{ value: 'WARNING', label: t('account.linkedEmails.levels.WARNING') },
			{ value: 'INFO', label: t('account.linkedEmails.levels.INFO') },
			{ value: 'DEBUG', label: t('account.linkedEmails.levels.DEBUG') },
		]);

		const periodOptions = computed(() => [
			{ value: 15, label: t('account.linkedEmails.periods.15') },
			{ value: 30, label: t('account.linkedEmails.periods.30') },
			{ value: 60, label: t('account.linkedEmails.periods.60') },
			{ value: 180, label: t('account.linkedEmails.periods.180') },
			{ value: 360, label: t('account.linkedEmails.periods.360') },
			{ value: 720, label: t('account.linkedEmails.periods.720') },
			{ value: 1440, label: t('account.linkedEmails.periods.1440') },
		]);

		const updateAccounts = () => {
			isLoading.value = true;
			ApiAlertsEmail.alertsEmailAccountsGetList(data => {
				accounts.value = toCamelCaseList(data.accounts || []);
				smtpReady.value = Boolean(data.smtp_ready);
				isLoading.value = false;
			});
		};
		onMounted(updateAccounts);

		const simpleHandler = successMessage => {
			return response => {
				if (response.status === 'ok') {
					message.success(t('account.linkedEmails.' + successMessage));
				} else {
					showErrorMessage(response.errors);
				}
				isLoading.value = false;
				updateAccounts();
			};
		};

		const accountUpdateHandler = response => {
			if (response.status !== 'ok') {
				showErrorMessage(response.errors);
				updateAccounts();
			}
		};

		const accountUpdate = record => {
			ApiAlertsEmail.alertsEmailAccountsUpdate(record, accountUpdateHandler);
		};

		const accountDelete = record => {
			isLoading.value = true;
			ApiAlertsEmail.alertsEmailAccountsDelete(record.email, simpleHandler('accountDeleted'));
		};

		const accountTest = record => {
			ApiAlertsEmail.alertsEmailAccountsTest(record.email, simpleHandler('accountTestSent'));
		};

		const accountResendVerification = record => {
			ApiAlertsEmail.alertsEmailAccountsResendVerification(
				record.email,
				simpleHandler('verificationEmailSent'),
			);
		};

		const onAccountAdd = () => {
			if (!smtpReady.value) {
				return;
			}
			validated.value = true;
			validate().then(() => {
				isLoading.value = true;
				ApiAlertsEmail.alertsEmailAccountsCreate(formState.newEmail, response => {
					if (response.status === 'ok') {
						message.success(t('account.linkedEmails.newEmailAdded'));
						formState.newEmail = '';
					} else {
						showErrorMessage(response.errors);
					}
					isLoading.value = false;
					updateAccounts();
				});
			}).catch(() => {
				isLoading.value = false;
			});
		};

		const validateInfosNewEmail = computed(() => {
			return validated.value ? validateInfos.newEmail : null;
		});
		watch(
			() => formState.newEmail,
			() => { validated.value = false; },
		);

		return {
			isLoading,
			accounts,
			smtpReady,
			formState,
			validateInfosNewEmail,
			columns: Consts.linkedEmailsTableColumnsComp,
			levelOptions,
			periodOptions,
			latinCharactersChecker: Validators.latinCharactersChecker,
			accountUpdate,
			accountDelete,
			accountTest,
			accountResendVerification,
			onAccountAdd,
		};
	},
};
</script>
